import { type LoaderFunctionArgs, json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { endOfDay, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  Area,
  ResponsiveContainer,
  type TooltipProps,
} from 'recharts';

import { type BaseAxisProps } from 'recharts/types/util/types';
import { LineChartLegend } from '#app/components/line-chart/line-chart-legend.tsx';
import {
  LineChartTooltip,
  type SiblingChartTooltip,
} from '#app/components/line-chart/line-chart-tooltip.tsx';
import { Slider } from '#app/components/ui/slider.tsx';
import { SyncLineChartModel } from '#app/models/sync-line-chart/index.ts';
import {
  type SchemaElement,
  type SyncLineChartSchema,
  type SyncLineChartData,
} from '#app/models/sync-line-chart/types.ts';
import { WidgetCard } from '#app/routes/widgets+/__widget-card.tsx';
import { WidgetPageContainer } from '#app/routes/widgets+/__widget-page-container.tsx';
import { getDefaultEndDate, getDefaultStartDate } from '#app/utils/dates.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { getWidgetData } from '#app/utils/get-widget-data.ts';
import { getDomainUrl } from '#app/utils/misc.tsx';
import { type WidgetProps } from './__widget-types.ts';
import { sanitizeOptions } from './__widget-utils.ts';

export async function loader({ request }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url);
  const origin = getDomainUrl(request);
  const options = sanitizeOptions(searchParams, {
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(searchParams),
  });
  const syncLineChartModel = new SyncLineChartModel(
    options.startDate,
    options.endDate,
  );

  const lineChartConfig = await prisma.chart.findFirst({
    where: {
      id: options.widgetId,
    },
  });

  if (!lineChartConfig) {
    throw new Response('Not found', { status: 404 });
  }
  try {
    const rawData = await getWidgetData<SyncLineChartData>(
      lineChartConfig,
      origin,
      { startDate: options.startDate, endDate: options.endDate },
    );

    const chart = syncLineChartModel.buildChart(lineChartConfig, rawData);

    return json(
      {
        chart,
        options,
        schema: lineChartConfig.schema,
      },
      {
        headers: {
          'Cache-Control':
            process.env.NODE_ENV === 'development'
              ? 'no-cache'
              : 'immutable, public, max-age=3600',
        },
      },
    );
  } catch (e) {
    console.error((e as Error).message);
    throw new Response('Internal Server Error', { status: 500 });
  }
}

export default function SynchronizedLineChartPage() {
  const data = useLoaderData<typeof loader>();
  const { chart, options } = data;
  const {
    width = 800,
    theme,
    type,
    startDate,
    endDate,
    dashboardSlug,
  } = options;

  return (
    <WidgetPageContainer
      type={type}
      width={width}
      startDate={startDate}
      endDate={endDate}
      dashboardSlug={dashboardSlug}
    >
      <SynchronizedLineChart
        theme={theme}
        readonly={true}
        startDate={startDate}
        endDate={endDate}
        data={chart.data}
        id={chart.id}
        title={chart.title}
        showExport={false}
        schema={chart.schema as SyncLineChartSchema}
      />
    </WidgetPageContainer>
  );
}

type ChartOptions = {
  xAxis?: BaseAxisProps;
  yAxis?: BaseAxisProps;
  colors: {
    patternColor: string;
    textColor: string;
    gridColor: string;
    axisColor: string;
  };
  readonly?: boolean;
};

export function SynchronizedLineChart({
  id,
  data,
  theme,
  startDate,
  endDate,
  showExport,
  domain = [
    getUnixTime(zonedTimeToUtc(startDate, 'Europe/Madrid')),
    getUnixTime(zonedTimeToUtc(endOfDay(endDate), 'Europe/Madrid')),
  ],
  title = 'Synchronized Line Chart',
  readonly,
  onSelectExport,
  schema,
}: WidgetProps<SyncLineChartData> & {
  startDate: string;
  endDate: string;
  domain?: [number, number];
  schema: SyncLineChartSchema;
}) {
  const [range, setRange] = useState<[number, number]>(domain);
  const [width, setWidth] = useState<string | number>('100%');
  const [siblingChart, setSiblingChart] = useState<
    SiblingChartTooltip | undefined
  >(undefined);

  const textColor = theme !== 'light' ? '#ececec' : '#1a1a25';
  const gridColor = theme !== 'light' ? '#323246' : '#9292a6';
  const axisColor = theme !== 'light' ? '#9292a6' : '#323246';
  const patternColor = '#9292a6';

  const highlightedRanges: [number, number][] = [];
  let start = -1;
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    if (d.alert) {
      if (start === -1) {
        start = i;
      }

      if (start !== -1 && i === data.length - 1) {
        const first = data[start].datetime;
        const last = data[i].datetime;
        highlightedRanges.push([first, last]);
        start = -1;
      }
      continue;
    }
    if (start !== -1) {
      const first = data[start].datetime;
      const last = data[i - 1].datetime;
      highlightedRanges.push([first, last]);
      start = -1;
    }
  }

  const options = {
    colors: {
      patternColor,
      textColor,
      gridColor,
      axisColor,
    },
    readonly,
    xAxis: {
      domain: range,
      allowDataOverflow: true,
    },
  };

  const legendMarks = uniqBy(
    [...(schema?.top?.marks ?? []), ...schema?.bottom?.marks],
    'title',
  );

  const onTooltipChange = useCallback(
    (ev: TooltipProps<any, any>) =>
      setSiblingChart((siblingChart) => {
        const getValue = (d: any, i: number): number | undefined =>
          d?.payload?.[i]?.value;
        if (
          getValue(siblingChart, 0) === getValue(ev, 0) &&
          getValue(siblingChart, 1) === getValue(ev, 1)
        ) {
          return siblingChart;
        }
        return {
          payload: ev?.payload,
          schemaElement: schema.top as SchemaElement,
        };
      }),
    [setSiblingChart, schema.top],
  );

  return (
    <WidgetCard
      id={id}
      data={data}
      title={title}
      readonly={readonly}
      showExport={showExport}
      onSelectExport={onSelectExport}
      legend={
        <LineChartLegend marks={legendMarks} patternColor={patternColor} />
      }
      widgetType="synchronized-line-chart"
    >
      {schema.top && (
        <LineChart
          id="top"
          syncId={id}
          aspect={4}
          onGridLayout={(dimensions) => setWidth(dimensions.width)}
          data={data}
          options={{
            ...options,
            xAxis: {
              ...options.xAxis,
              tick: false,
            },
          }}
          schemaElement={schema.top}
          onTooltipChange={onTooltipChange}
        />
      )}

      <LineChart
        id="bottom"
        syncId={id}
        data={data}
        aspect={schema.top != null ? 4 : 2}
        onGridLayout={
          schema?.top == null
            ? (dimensions) => setWidth(dimensions.width)
            : undefined
        }
        options={options}
        schemaElement={schema.bottom}
        siblingChart={siblingChart}
      />

      {!readonly && (
        <div className="z-0 flex w-full items-center justify-end">
          <div className="rounded-lg border border-border px-3 py-4">
            <div style={{ width }}>
              <Slider
                onValueChange={(values) => {
                  setRange(values as [number, number]);
                }}
                defaultValue={domain}
                min={domain[0]}
                max={domain[1]}
                step={900}
                activeRange={range}
                highlightedRanges={highlightedRanges}
                trackClassName="bg-highlight rounded-none"
                rangeClassName="bg-highlight rounded-none"
                highlightedRangeClassName="bg-warning"
                thumbClassName="bg-primary rounded-[2px] w-3 h-6"
              />
            </div>
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

function getComposedChartElement(
  element: SchemaElement['marks'][number],
  options: ChartOptions,
) {
  switch (element.type) {
    case 'line': {
      const isDotted = element.dashStyle.startsWith('dotted');
      const line = (
        <Line
          isAnimationActive={!options.readonly}
          type="monotone"
          dataKey={element.dataKey}
          stroke={element.stroke}
          strokeLinecap={isDotted ? 'round' : undefined}
          strokeDasharray={isDotted ? '1, 6' : undefined}
          strokeWidth={3}
          dot={false}
          activeDot={false}
        />
      );

      if (element.dashStyle === 'dotted2') {
        return (
          <>
            {line}
            <Line
              isAnimationActive={!options.readonly}
              tooltipType="none"
              type="monotone"
              dataKey={element.dataKey}
              stroke={element.stroke}
              strokeWidth={1}
              dot={false}
              activeDot={false}
            />
          </>
        );
      }

      return line;
    }
    case 'area': {
      const isPattern = element.dashStyle === 'pattern';
      return (
        <Area
          tooltipType="none"
          isAnimationActive={!options.readonly}
          dataKey={element.dataKey}
          stroke={isPattern ? 'transparent' : element.stroke}
          fill={isPattern ? 'url(#diagonalHatch)' : element.fill}
          activeDot={false}
        />
      );
    }
    default:
      return null;
  }
}

function LineChart({
  id,
  syncId,
  data,
  options,
  aspect,
  schemaElement,
  onGridLayout,
  siblingChart,
  onTooltipChange,
}: {
  id: string;
  syncId: string;
  data: any;
  aspect: number;
  options: ChartOptions;
  schemaElement: SchemaElement;
  onGridLayout?: (dimensions: DOMRect) => void;
  siblingChart?: SiblingChartTooltip;
  onTooltipChange?: (props: TooltipProps<any, any>) => void;
}) {
  const { colors, readonly } = options;
  const [dimensions, setDimensions] = useState<DOMRect | null>(null);

  const onResize = () => {
    const el = document.querySelector('.recharts-cartesian-grid');
    if (el) {
      const dimensions = el.getBoundingClientRect();
      setDimensions(dimensions);
    } else if (dimensions != null) {
      setDimensions(null);
    }
  };

  useEffect(() => {
    if (dimensions != null) {
      onGridLayout?.(dimensions);
    }
  }, [onGridLayout, dimensions]);

  const height = 320;
  const margin = {
    right: 48,
    left: 52,
    bottom: 8,
    top: 8,
  };

  const values = schemaElement.marks.map((mark) =>
    Math.max(
      ...data.map((d: Record<string, number | string>) =>
        typeof d[mark.dataKey] === 'number' ? d[mark.dataKey] : 0,
      ),
    ),
  );

  const domainY = [0, Math.max(...values) * 1.2];

  const areaMarks = schemaElement.marks.filter((mark) => mark.type === 'area');

  const formattedData = data.map(
    (d: Record<string, number | string | undefined>) => {
      return areaMarks.reduce(
        (acc, mark) => {
          let value = d[mark.dataKey];
          if (typeof value == null) {
            return acc;
          }

          if (typeof d[mark.dataKey] === 'boolean') {
            value = d[mark.dataKey] ? domainY[1] : 0;
          }

          return {
            ...acc,
            [mark.dataKey]: value,
          };
        },
        { ...d },
      );
    },
  );

  const tooltipContent = useCallback(
    (props: TooltipProps<any, any>) => {
      if (onTooltipChange) {
        onTooltipChange(props);
        return null;
      }

      return (
        <LineChartTooltip
          {...props}
          siblingChart={siblingChart}
          schemaElement={schemaElement}
          parentDimensions={dimensions as DOMRect}
        />
      );
    },
    [onTooltipChange, schemaElement, siblingChart, dimensions],
  );

  const axisFontSize = 12;
  let xAxisStart: number | null = null;
  let xAxisEnd: number | null = null;

  if (
    options.xAxis?.domain != null &&
    Array.isArray(options.xAxis?.domain) &&
    typeof options.xAxis?.domain[0] === 'number' &&
    typeof options.xAxis?.domain[1] === 'number'
  ) {
    xAxisStart = options.xAxis?.domain[0];
    xAxisEnd = options.xAxis?.domain[1];
  }

  let isShowingMultipleDays = false;
  if (xAxisStart != null && xAxisEnd != null) {
    isShowingMultipleDays = xAxisEnd - xAxisStart > 3600 * 24;
  }

  const axisXTicks = useMemo(() => {
    if (xAxisStart == null || xAxisEnd == null) {
      return [];
    }

    const startTimestamp = getUnixTime(startOfDay(fromUnixTime(xAxisStart)));
    const hours = Math.ceil((xAxisEnd - xAxisStart) / 3600);
    let ticksPerHour = 1; // every hour
    let secondsInterval = 3600 * 23; // 23hrs in seconds

    if (!isShowingMultipleDays) {
      ticksPerHour = 2; // every 30min
      secondsInterval = 1800; // 30min in seconds;
    }

    if (xAxisEnd - xAxisStart < 3600 * 4) {
      ticksPerHour = 4; // 4: every 15min
      secondsInterval = 900; // 15min in seconds
    }
    if (xAxisEnd - xAxisStart < 3600 * 2) {
      ticksPerHour = 6; // 6: every 10min
      secondsInterval = 600; // 5min in seconds
    }

    if (xAxisEnd - xAxisStart < 3600) {
      ticksPerHour = 12; // 12: every 5min
      secondsInterval = 500; // 1min in seconds
    }

    const everyXSeconds = new Array(hours * ticksPerHour)
      .fill(0)
      .map((_, i) => (startTimestamp as number) + i * secondsInterval)
      .filter(
        (timestamp) =>
          timestamp >= (xAxisStart as number) &&
          timestamp <= (xAxisEnd as number),
      );
    let labelWidth = axisFontSize * 6;
    if (isShowingMultipleDays) {
      labelWidth = axisFontSize * 10;
    }
    let valuesX;
    if (dimensions) {
      const { width } = dimensions;
      const containerWidth = width - margin.left - margin.right;

      const ticksX = Math.floor(containerWidth / labelWidth);
      const stepX = Math.max(Math.floor(everyXSeconds.length / ticksX), 1);
      valuesX = everyXSeconds;
      if (everyXSeconds.length > ticksX) {
        valuesX = everyXSeconds.filter((_, i) => i % stepX === 0);
      }
    } else {
      valuesX = everyXSeconds.filter((_, i) => i % 2 === 0);
    }

    return valuesX;
  }, [
    margin.left,
    margin.right,
    dimensions,
    axisFontSize,
    xAxisStart,
    xAxisEnd,
    isShowingMultipleDays,
  ]);

  return (
    <ResponsiveContainer
      id={id}
      aspect={aspect}
      width="100%"
      onResize={onResize}
      className="relative z-10"
    >
      <ComposedChart
        height={height}
        data={formattedData}
        syncId={syncId}
        margin={margin}
      >
        <defs>
          <pattern
            id="diagonalHatch"
            patternUnits="userSpaceOnUse"
            width="4"
            height="4"
          >
            <path
              d="M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2"
              strokeWidth={1}
              stroke={colors.patternColor}
            />
          </pattern>
        </defs>
        <CartesianGrid
          vertical={false}
          stroke={colors.gridColor}
          strokeWidth={1}
        />
        <XAxis
          dataKey="datetime"
          type="number"
          stroke={colors.axisColor}
          tickLine={false}
          tickMargin={8}
          tick={{
            fill: colors.textColor,
            fontWeight: 300,
            fontSize: axisFontSize,
            fontFamily: 'Roboto Mono',
          }}
          tickFormatter={(unixTime) => {
            if (unixTime == null) {
              return '';
            }
            const date = fromUnixTime(unixTime);
            const time = format(date, 'HH:mm', { timeZone: 'Europe/Madrid' });
            if (isShowingMultipleDays) {
              const dateMonth = format(date, 'dd/MM', {
                timeZone: 'Europe/Madrid',
              });

              return `${time}h ${dateMonth}`;
            }

            return `${time}h`;
          }}
          ticks={axisXTicks}
          scale="linear"
          {...options.xAxis}
        />
        <YAxis
          scale="linear"
          stroke={colors.axisColor}
          tickLine={false}
          axisLine={false}
          tickMargin={20}
          interval={0}
          tick={{
            fill: colors.textColor,
            fontWeight: 300,
            fontSize: axisFontSize,
            fontFamily: 'Roboto Mono',
          }}
          domain={domainY}
          {...options.yAxis}
        />
        <Tooltip
          isAnimationActive={!readonly}
          cursorStyle={{
            stroke: colors.axisColor,
            strokeWidth: 2,
          }}
          content={tooltipContent}
        />
        {schemaElement.marks.map((mark) => (
          <React.Fragment key={mark.dataKey}>
            {getComposedChartElement(mark, options)}
          </React.Fragment>
        ))}
      </ComposedChart>
      <div className="absolute bottom-0 left-0 top-0 flex w-[100px] items-start justify-start">
        <div className="flex h-full max-w-[20px] items-center justify-start">
          <p className="-translate-x-1/2 -translate-y-1/2 -rotate-90 whitespace-nowrap pb-3 text-body text-primary">
            {schemaElement.yAxis?.label}
          </p>
        </div>
        <span
          className="min-w-[20px] border border-r-0 border-accent-foreground"
          style={{
            marginTop: margin.top,
            marginBottom: margin.bottom,
            height: dimensions?.height ?? '90%',
          }}
        />
      </div>
    </ResponsiveContainer>
  );
}
